import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Globe2,
  ShieldCheck,
  Scale,
  Activity,
  Users,
  FileText,
  ArrowRight,
  Network,
  BarChart3,
  HeartHandshake,
} from "lucide-react";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";

const HeroBlock = () => (
  <section className="relative pt-32 pb-24 overflow-hidden">
    <div className="absolute inset-0 opacity-60"><NeuralNetworkBackground density={28} /></div>
    <ScaleWatermark />
    <div className="turquoise-aura -top-32 left-0 opacity-50" />
    <div className="gold-aura -bottom-40 right-0 opacity-40" />
    <div className="grain-overlay" />
    <div className="container relative z-10 mx-auto px-6 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
        <div className="aj-chip mb-6"><Building2 className="w-3 h-3" /> Institutional Partnerships</div>
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05]">
          Digital justice infrastructure for{" "}
          <span className="italic text-aj-gradient">governments, bar associations</span> &{" "}
          <span className="text-gold-gradient">development partners.</span>
        </h1>
        <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
          AccessJustice.Global is more than a legal platform. It is the operating layer for nation-scale
          legal aid, lawyer oversight, NGO program delivery and SDG 16 measurement.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/contact" data-testid="gov-cta-partner" className="btn-aj-primary">
            Partner With Us <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" data-testid="gov-cta-brief" className="btn-aj-secondary">
            Request Institutional Brief
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

const stakeholders = [
  {
    title: "Governments & Ministries",
    icon: Building2,
    points: ["Expand citizen legal aid programs", "Track service delivery and outcomes", "Strengthen rule of law", "Generate justice impact data", "Promote accountability and citizen trust"],
  },
  {
    title: "Bar Associations",
    icon: Scale,
    points: ["Lawyer verification and enrollment status", "Complaint review and ethics monitoring", "Misconduct reporting and discipline", "Professional standards compliance", "Suspension recommendations"],
  },
  {
    title: "Development Partners",
    icon: Globe2,
    points: ["SDG 16 measurable progress", "Cross-border justice infrastructure", "AI for Good deployment", "Capacity-building data", "Donor-grade transparency"],
  },
  {
    title: "Legal Aid Organizations",
    icon: HeartHandshake,
    points: ["Beneficiary eligibility automation", "Lawyer assignment at scale", "Case lifecycle tracking", "Funding utilization reporting", "Impact-aligned outcomes"],
  },
];

const ForGovernments = () => (
  <div className="bg-ink text-white" data-testid="for-governments-page">
    <HeroBlock />

    <Section eyebrow="Who We Serve" title={<>Built for the institutions that <span className="italic text-aj-gradient">shape justice systems.</span></>}>
      <div className="grid md:grid-cols-2 gap-5">
        {stakeholders.map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
            <GlassCard glow className="h-full">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gold-400/10 border border-gold-400/30 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-gold-300" />
                </div>
                <h3 className="font-playfair text-2xl text-white">{s.title}</h3>
              </div>
              <ul className="space-y-2.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 mt-2 bg-turquoise rounded-full shrink-0" />
                    <span className="font-inter text-sm text-white/75">{p}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Capabilities */}
    <Section eyebrow="Institutional Capabilities" title={<>Operational, analytical, <span className="italic text-aj-gradient">accountable.</span></>} aurora>
      <div className="grid md:grid-cols-3 gap-5">
        {[
          { icon: Network, t: "Lawyer Marketplace API", d: "Plug in verified lawyer pools across jurisdictions." },
          { icon: ShieldCheck, t: "NBA Compliance Portal", d: "Ethics dashboards, suspension workflows, audit trails." },
          { icon: Activity, t: "Case Lifecycle Telemetry", d: "From intake to judgment — live metrics by region." },
          { icon: BarChart3, t: "Donor & SDG Reporting", d: "SDG 16 dashboards. Beneficiary demographics. Outcomes." },
          { icon: Users, t: "Legal Aid Engine", d: "Wallet-funded support for vulnerable populations." },
          { icon: FileText, t: "Policy Intelligence", d: "Case-data signals that inform justice reform." },
        ].map((c, i) => (
          <motion.div key={c.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.05 }}>
            <GlassCard glow className="h-full">
              <c.icon className="w-7 h-7 text-turquoise mb-4" />
              <h3 className="font-playfair text-xl text-white mb-2">{c.t}</h3>
              <p className="font-inter text-sm text-white/60 leading-relaxed">{c.d}</p>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Final CTA */}
    <Section className="border-t border-white/8">
      <div className="text-center max-w-3xl mx-auto">
        <Eyebrow>Let&apos;s build justice infrastructure</Eyebrow>
        <h2 className="font-playfair text-4xl md:text-5xl text-white mt-4 mb-8 leading-tight">
          Begin a conversation about your{" "}
          <span className="italic text-aj-gradient">justice strategy.</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact" data-testid="gov-final-partner" className="btn-aj-primary">
            Schedule a Partnership Briefing
          </Link>
          <Link to="/about" data-testid="gov-final-about" className="btn-aj-secondary">
            About AccessJustice
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default ForGovernments;
